import { createStore } from "zustand/vanilla";
import { devtools } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import type { AppState, AgUiTool } from "@strands/agent-transport";
import type { FeedbackInfo, MessageInfo, Part, ToolPart } from "../types";
import { INITIAL_SESSION_STATE, type SessionState } from "./session-state";

// ---------------------------------------------------------------------------
// Store interface
// ---------------------------------------------------------------------------

export interface ChatStore extends SessionState {
  activeSessionId: string | null;
  sessionCache: Record<string, SessionState>;
  /**
   * Client-declared frontend tools (`question`, `calculator`, …) included
   * in every `RunAgentInput.tools`. Lives on the top-level store, not in
   * SessionState — ChatPage registers them once at mount, and a
   * `setActiveSession` (which Object.assigns a fresh SessionState slice
   * over the active state) must NOT wipe this list, otherwise the new
   * session loses `question` until the page is remounted.
   */
  pendingTools: AgUiTool[];
  /**
   * Extra payload merged into `RunAgentInput.forwardedProps` on every run.
   * Lives on the store (not React state) so transport callbacks can read it
   * without re-rendering, and so isolated stores (e.g. AgentBuilder's test
   * panel) keep their own props without leaking into the main chat.
   */
  runtimeForwardedProps: Record<string, unknown>;

  // ----- Session lifecycle -----
  setActiveSession: (id: string | null) => void;
  saveToCache: () => void;
  restoreFromCache: (id: string) => void;
  resetSession: () => void;

  // ----- Messages & parts -----
  pushMessage: (msg: MessageInfo) => void;
  pushMessages: (msgs: MessageInfo[]) => void;
  setPartsForMessage: (messageId: string, parts: Part[]) => void;

  /** Patch the feedback object of the message whose traceId matches.
   *  `null` clears the vote (used when DELETE succeeds or on optimistic
   *  rollback). */
  setFeedbackForTrace: (traceId: string, feedback: FeedbackInfo | null) => void;

  // ----- Streaming -----
  setStreaming: (v: boolean) => void;

  // ----- App state (AG-UI STATE_SNAPSHOT / client-owned keys) -----
  setAppState: (state: AppState) => void;
  updateAppStateKey: (key: string, value: unknown) => void;

  // ----- Tools -----
  registerTool: (tool: AgUiTool) => () => void;

  // ----- Runtime forwarded props -----
  setRuntimeForwardedProps: (props: Record<string, unknown>) => void;

  // ----- HITL -----
  /**
   * Flip a tool part to completed + append empty assistant message.
   * Single atomic update (critical for HITL — see useChat.ts lines 555-570).
   */
  completeToolAndPushAssistant: (
    toolCallId: string,
    result: string,
    sessionId: string,
  ) => MessageInfo;
}

// ---------------------------------------------------------------------------
// Store factory
// ---------------------------------------------------------------------------

/**
 * Build a fresh chat-store instance. Each call returns an independent store
 * with its own state and action closures — used by `<ChatStoreProvider>` to
 * lazily provision per-subtree stores. AgentBuilder relies on this to run an
 * isolated test session that doesn't touch the main chat.
 */
export function createChatStore(devtoolsName = "strands-chat") {
  return createStore<ChatStore>()(
    devtools(
      immer((set, _get) => ({
        // ----- Initial state -----
        ...freshSessionState(),
        activeSessionId: null,
        sessionCache: {},
        pendingTools: [],
        runtimeForwardedProps: {},

        // ----- Session lifecycle -----
        setActiveSession: (id) =>
          set((s) => {
            // Save current to cache before switching
            if (s.activeSessionId) {
              s.sessionCache[s.activeSessionId] = extractSessionState(s);
            }
            s.activeSessionId = id;
            // Reset session fields
            Object.assign(s, freshSessionState());
          }),

        saveToCache: () =>
          set((s) => {
            if (s.activeSessionId) {
              s.sessionCache[s.activeSessionId] = extractSessionState(s);
            }
          }),

        restoreFromCache: (id) =>
          set((s) => {
            const cached = s.sessionCache[id];
            if (cached) {
              Object.assign(s, cached);
              delete s.sessionCache[id];
            }
          }),

        resetSession: () =>
          set((s) => {
            Object.assign(s, freshSessionState());
          }),

        // ----- Messages & parts -----
        pushMessage: (msg) =>
          set((s) => {
            s.messages.push(msg);
          }),

        pushMessages: (msgs) =>
          set((s) => {
            s.messages.push(...msgs);
          }),

        setPartsForMessage: (messageId, parts) =>
          set((s) => {
            s.partsMap[messageId] = parts;
          }),

        setFeedbackForTrace: (traceId, feedback) =>
          set((s) => {
            for (const m of s.messages) {
              if (m.traceId === traceId) {
                m.feedback = feedback;
              }
            }
          }),

        // ----- Streaming -----
        setStreaming: (v) =>
          set((s) => {
            s.streaming = v;
          }),

        // ----- App state -----
        setAppState: (state) =>
          set((s) => {
            s.appState = state;
          }),

        updateAppStateKey: (key, value) =>
          set((s) => {
            if (value === undefined) {
              delete s.appState[key];
            } else {
              s.appState[key] = value;
            }
          }),

        // ----- Tools -----
        registerTool: (tool) => {
          set((s) => {
            s.pendingTools = [...s.pendingTools.filter((t: AgUiTool) => t.name !== tool.name), tool];
          });
          // Return unregister callback (reads current state, not closure)
          return () => {
            set((s) => {
              s.pendingTools = s.pendingTools.filter((t: AgUiTool) => t.name !== tool.name);
            });
          };
        },

        setRuntimeForwardedProps: (props) =>
          set((s) => {
            s.runtimeForwardedProps = props;
          }),

        // ----- HITL -----
        completeToolAndPushAssistant: (toolCallId, result, sessionId) => {
          const assistantMsg: MessageInfo = {
            id: crypto.randomUUID(),
            role: "assistant",
            sessionId,
          };
          set((s) => {
            // Flip the tool part to completed
            for (const parts of Object.values(s.partsMap)) {
              for (const part of parts) {
                if (part.type === "tool" && part.id === toolCallId) {
                  (part as ToolPart).state.status = "completed";
                  (part as ToolPart).state.output = result;
                }
              }
            }
            // Append empty assistant message
            s.messages.push(assistantMsg);
            s.partsMap[assistantMsg.id] = [];
          });
          return assistantMsg;
        },
      })),
      { name: devtoolsName },
    ),
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Build a fresh copy of the per-session slice. We can't reuse
 * `INITIAL_SESSION_STATE` directly because it holds shared object/array
 * references — multiple store instances would mutate the same `appState` /
 * `toolCallArgsBuffer` and immediately bleed state between trees.
 */
function freshSessionState(): SessionState {
  return {
    ...INITIAL_SESSION_STATE,
    messages: [],
    partsMap: {},
    appState: {},
    toolCallArgsBuffer: {},
  };
}

function extractSessionState(s: ChatStore): SessionState {
  return {
    messages: s.messages,
    partsMap: s.partsMap,
    streaming: s.streaming,
    activeRunId: s.activeRunId,
    error: s.error,
    appState: s.appState,
    toolCallArgsBuffer: s.toolCallArgsBuffer,
    inReasoning: s.inReasoning,
  };
}

/**
 * StoreApi as returned by the factory — preserves the immer-wrapped
 * `setState` overload (which accepts a draft mutator that returns void).
 * Don't replace with `StoreApi<ChatStore>` — that loses the immer typing
 * and every `(s) => { s.x = y }` callsite breaks the build.
 */
export type ChatStoreApi = ReturnType<typeof createChatStore>;
